import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  createResourceLoader,
  MapEntry,
  Nullable,
  ResourceType,
  shouldGetResourceDataFromCache,
} from '@tager/web-core';
import { getMenuItemListByAlias, MenuItemType } from '@tager/web-modules';

import { AppState, AppThunk } from '@/store/store';
import { getCatalogMenu } from '@/services/requests';
import { CatalogMenuType } from '@/typings/model';

const catalogMenuLoader = createResourceLoader<Array<CatalogMenuType>>([]);

type State = {
  menuMap: Record<string, Array<MenuItemType>>;
  catalogMenu: ResourceType<Array<CatalogMenuType>>;
};

const initialState: State = {
  menuMap: {},
  catalogMenu: catalogMenuLoader.getInitialResource(),
};

const menuSlice = createSlice({
  name: 'menu',
  initialState,
  reducers: {
    /** Catalog Menu */
    catalogMenuRequestPending(state) {
      state.catalogMenu = catalogMenuLoader.pending();
    },
    catalogMenuRequestFulfilled(
      state,
      action: PayloadAction<Array<CatalogMenuType>>
    ) {
      state.catalogMenu = catalogMenuLoader.fulfill(action.payload);
    },
    catalogMenuRequestRejected(state) {
      state.catalogMenu = catalogMenuLoader.reject();
    },
    menuItemListAdded(
      state,
      action: PayloadAction<MapEntry<string, Array<MenuItemType>>>
    ) {
      state.menuMap[action.payload.key] = action.payload.value;
    },
  },
});

const { actions, reducer } = menuSlice;
export const {
  menuItemListAdded,
  catalogMenuRequestPending,
  catalogMenuRequestFulfilled,
  catalogMenuRequestRejected,
} = actions;

export default reducer;

export function getMenuItemListThunk(
  menuAlias: string,
  options?: {
    shouldInvalidate?: boolean;
  }
): AppThunk<Promise<Nullable<Array<MenuItemType>>>> {
  return async (dispatch, getState) => {
    try {
      const menuItemList = selectMenuItemListByAlias(getState(), menuAlias);

      if (!options?.shouldInvalidate && menuItemList) {
        return menuItemList;
      }

      const response = await getMenuItemListByAlias(menuAlias);

      dispatch(
        menuItemListAdded({
          key: menuAlias,
          value: response.data,
        })
      );

      return response.data;
    } catch (error) {
      return null;
    }
  };
}

export function selectMenuItemListByAlias(
  state: AppState,
  menuAlias: string
): Array<MenuItemType> | undefined {
  return state.tager.menus.menuMap[menuAlias];
}

export function getCatalogMenuThunk(options?: {
  shouldInvalidate?: boolean;
}): AppThunk<Promise<Array<CatalogMenuType>>> {
  return async (dispatch, getState) => {
    const catalogMenuResource = selectCatalogMenuResource(getState());
    const shouldGetDataFromCache = shouldGetResourceDataFromCache(
      catalogMenuResource,
      options?.shouldInvalidate
    );

    if (shouldGetDataFromCache) {
      return catalogMenuResource.data;
    }

    dispatch(catalogMenuRequestPending());

    try {
      const response = await getCatalogMenu();
      dispatch(catalogMenuRequestFulfilled(response.data));
      return response.data;
    } catch (error) {
      dispatch(catalogMenuRequestRejected());
      return [];
    }
  };
}

export function selectCatalogMenuResource(
  state: AppState
): ResourceType<Array<CatalogMenuType>> {
  return state.tager.menus.catalogMenu;
}
